import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { dismissToast, getSnapshot, subscribe, type ToastVariant } from './toast-store';

const VARIANT: Record<
  ToastVariant,
  { Icon: typeof Info; border: string; icon: string }
> = {
  success: { Icon: CheckCircle, border: 'border-l-green-500', icon: 'text-green-400' },
  error: { Icon: XCircle, border: 'border-l-red-500', icon: 'text-red-400' },
  warning: { Icon: AlertTriangle, border: 'border-l-yellow-500', icon: 'text-yellow-400' },
  info: { Icon: Info, border: 'border-l-blue-500', icon: 'text-blue-400' },
};

export function ToastHost() {
  const { t } = useTranslation();
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 z-[100] flex max-w-[360px] flex-col gap-2 ltr:right-4 rtl:left-4"
    >
      {toasts.map((toast) => {
        const v = VARIANT[toast.variant];
        return (
          <div
            key={toast.id}
            role="status"
            aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-xl border border-white/5 border-l-4 bg-[var(--bg-card)] px-4 py-3 shadow-2xl',
              v.border,
            )}
          >
            <v.Icon className={cn('mt-0.5 h-5 w-5 shrink-0', v.icon)} />
            <p className="flex-1 break-words text-sm text-[var(--text-primary)]">{toast.message}</p>
            <button
              type="button"
              onClick={() => dismissToast(toast.id)}
              aria-label={t('common.dismissNotification')}
              className="shrink-0 text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
